import type { Body, Meta, Uppy, UppyEventMap, UppyFile as UppyFileFromUppy, UppyOptions } from '@uppy/core';
// disabling camel case because many of these property names come from Uppy
import type { AssemblyResult } from '@uppy/transloadit';

import type { Assembly, BaseAssembly } from 'bundles/authoring/common/types/Upload';

declare module '@uppy/core' {
  export interface UppyEventMap<M extends Meta, B extends Body> {
    // custom events from our own plugin
    'assetCreation:complete': (assetIds: string[], assembly: Assembly) => void;
    'assetCreation:error': (error: Error, assembly: Assembly) => void;
  }
}

declare module '@uppy/utils/lib/UppyFile' {
  export interface UppyFile<M extends Meta, B extends Body> {
    attemptId?: string | null;
  }
}

export type CoreUppyMeta = {
  relativePath?: string | null;
  attemptId?: string | null;
  assembly_url?: string | null;
} & Meta;

export type TBody = { attemptId?: string | null } & Body;

export type UppyFile = UppyFileFromUppy<CoreUppyMeta, TBody>;

export type TMeta = CoreUppyMeta;

export type UppyOptionsWithMeta = UppyOptions<TMeta, TBody>;
export type OnBeforeUpload = UppyOptionsWithMeta['onBeforeUpload'];
export type onBeforeFileAdded = UppyOptionsWithMeta['onBeforeFileAdded'];

export type UppyFiles = { [key: string]: UppyFile };

export type UppyEventHandlers = UppyEventMap<TMeta, TBody>;
export type UppyEvents = Partial<UppyEventHandlers>;

// no longer exported from uppy/transloadit, defining frmo uppy events here
export type TransloaditAssemblyCreatedCallback = UppyEvents['transloadit:assembly-created'];
export type TransloaditUploadedCallback = UppyEvents['transloadit:upload'];
export type TransloaditAssemblyExecutingCallback = UppyEvents['transloadit:assembly-executing'];
export type TransloaditResultCallback = UppyEvents['transloadit:result'];
export type TransloaditCompleteCallback = UppyEvents['transloadit:complete'];

export type UppyWithMeta = Uppy<TMeta, TBody>;

export type UppyProps = {
  autoProceed?: boolean;
  events?: UppyEvents;
};

export type UppyRef = {
  current?: UppyWithMeta;
};

// copy/pasted from @uppy/core since it isn't exported but we need to access to this type
export interface DeterminateFileProcessing {
  mode: 'determinate';
  message: string;
  value: number;
}
// copy/pasted from @uppy/core since it isn't exported but we need to access to this type
export interface IndeterminateFileProcessing {
  mode: 'indeterminate';
  message?: string;
  value?: 0;
}
// copy/pasted from @uppy/core since it isn't exported but we need to access to this type
export type FileProcessingInfo = IndeterminateFileProcessing | DeterminateFileProcessing;

// copy/pasted from @uppy/core since it isn't exported but we need to access to this type
interface FileProgressBase {
  uploadComplete?: boolean;
  percentage?: number; // undefined if we don't know the percentage (e.g. for files with `bytesTotal` null)
  // note that Companion will send `bytesTotal` 0 if unknown size (not `null`).
  // this is not perfect because some files can actually have a size of 0,
  // and then we might think those files have an unknown size
  // todo we should change this in companion
  bytesTotal: number | null;
  preprocess?: FileProcessingInfo;
  postprocess?: FileProcessingInfo;
}

// copy/pasted from @uppy/core since it isn't exported but we need to access to this type
// FileProgress is either started or not started. We want to make sure TS doesn't
// let us mix the two cases, and for that effect, we have one type for each case:
export type FileProgressStarted = FileProgressBase & {
  uploadStarted: number;
  bytesUploaded: number;
};

export type FileUploadProgress = FileProgressStarted;

export type UploadCompletion = {
  transloadit: unknown;
  successful: Array<UppyFile>;
  failed: Array<UppyFile>;
};

export type AssetOptions = {
  pdfTemplate?: string;
  language?: string;
  name: string;
};

export type FileAttempt = UppyFileFromUppy<TMeta, TBody>;

export type AssemblyAttempt = Assembly & {
  attemptId: string;
};

export type ValidAssetSources = 'Local' | 'GoogleDrive' | 'OneDrive' | 'Dropbox' | 'Url' | 'Zoom';

// this maps to the BE here https://github.com/webedx-spark/infra-services/blob/main/libs/models/src/main/pegasus/org/coursera/assetservice/AssetData.courier
export type SuccessAsset = {
  mime: string;
  filename: string;
  size: number;
  sslUrl: string;
  url: string;
  sourceType: ValidAssetSources;
  // extra fields for video, audio, etc.
  duration?: number;
  pageCount?: number;
  audioBitRate?: number;
  subtitleType?: string;
};

export type TransloaditAssetFile = AssemblyResult & {
  user_meta: TMeta;
};

export type AssetAssembly = BaseAssembly & {
  results: {
    virus_scanned: Array<TransloaditAssetFile>;
    [key: string]: Array<TransloaditAssetFile>;
  };
};

// Mismatch between the required props in uppy and the uppy docs / what's actually needed.
export type Locale = Pick<Uppy['locale'], 'strings'>;

// not exposed by uppy anymore
export type InternalMetadata = { name: string; type?: string };

export const isAssetAssembly = (assembly?: BaseAssembly): assembly is AssetAssembly => {
  return !!(assembly?.results?.virus_scanned?.[0] as TransloaditAssetFile)?.user_meta?.attemptId;
};
